import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { MatrixLogo } from '@/components/icons/MatrixLogo';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { MatrixOverlay } from '@/components/ui/MatrixOverlay';
import { Link } from 'react-router-dom';
import { UserTokenSelector } from '@/components/token/UserTokenSelector';
import {
  Lock,
  Unlock,
  Shield,
  Check,
  X,
  RefreshCw,
  AlertTriangle,
  DollarSign,
  RefreshCcw,
  Percent,
  Wallet,
  FileCode,
  ArrowUpCircle,
  ArrowDownCircle,
  Clock,
  Coins,
  Tag,
  User,
  BarChart,
  CheckCircle2,
  XCircle,
  Network,
  ArrowsUpDown,
  ArrowLeftRight,
  Droplet,
  Download,
  BookOpen,
  Rocket,
  Flame,
  Wrench,
  Search,
  CheckCircle,
  BarChart3Icon,
  LockIcon,
  FileCode2
} from 'lucide-react';
import { useContractScannerSettings } from '@/providers/ContractScannerSettingsProvider';
import { useFirebaseFees } from '@/providers/FirebaseFeeProvider';
import { useAccount, useWalletClient, useNetwork } from 'wagmi';
import { Helmet } from 'react-helmet-async';
import { useTheme } from "@/providers/ThemeProvider";

// Common function name variations for better detection
const FUNCTION_VARIATIONS = {
  OWNER: ['owner', 'getOwner', '_owner', 'contractOwner', 'tokenOwner'],
  
  REFLECTION_DISTRIBUTION: [
    'reflectionDistribution',
    '_reflectionDistribution',
    'getReflectionDistribution'
  ],
  LIQUIDITY_DISTRIBUTION: [
    'autoLiquidityDistribution',
    '_autoLiquidityDistribution',
    'getLiquidityDistribution'
  ],
  BURN_DISTRIBUTION: [
    'burnDistribution',
    '_burnDistribution',
    'getBurnDistribution'
  ],
  MARKETING_DISTRIBUTION: [
    'marketingDistribution',
    '_marketingDistribution',
    'getMarketingDistribution'
  ],
  DEVELOPMENT_DISTRIBUTION: [
    'developmentDistribution',
    '_developmentDistribution',
    'getDevelopmentDistribution'
  ],
  
  BUY_TAX: [
    'buyTax',
    '_buyTax',
    'getBuyTax',
    'buyFee',
    '_buyFee'
  ],
  SELL_TAX: [
    'sellTax',
    '_sellTax',
    'getSellTax',
    'sellFee',
    '_sellFee'
  ],
  
  MAX_WALLET: [
    'maxWalletAmount',
    '_maxWalletAmount',
    'maxWallet',
    'maxWalletSize'
  ],
  MAX_BUY: [
    'maxBuyAmount',
    '_maxBuyAmount',
    'maxBuy',
    'maxBuyTxAmount'
  ],
  MAX_SELL: [
    'maxSellAmount',
    '_maxSellAmount',
    'maxSell',
    'maxSellTxAmount'
  ],
  TRADING_DELAY: [
    'tradingDelay',
    '_tradingDelay',
    'getDelay'
  ]
};

// ABI for basic contract interactions
const TAX_ABI = [
  // Basic Token Info
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function totalSupply() view returns (uint256)",
  "function owner() view returns (address)",
  
  // DEX Info
  "function pancakePair() view returns (address)",
  "function pancakeRouter() view returns (address)",
  "function uniswapPair() view returns (address)",
  "function uniswapRouter() view returns (address)",
  "function traderJoePair() view returns (address)",
  "function traderJoeRouter() view returns (address)",
  "function tradingEnabled() view returns (bool)",
  "function permanentTradingEnabled() view returns (bool)",
  "function tradingEnabledLocked() view returns (bool)",
  
  // Tax Management
  "function buyTax() view returns (uint256)",
  "function sellTax() view returns (uint256)",
  "function _buyTax() view returns (uint256)",
  "function _sellTax() view returns (uint256)",
  "function buyTaxLocked() view returns (bool)",
  "function sellTaxLocked() view returns (bool)",
  
  // Tax Distribution
  "function taxDistribution() view returns (uint256 reflection, uint256 autoLiquidity, uint256 burn, uint256 marketing, uint256 development)",
  "function _taxDistribution() view returns (uint256 reflection, uint256 autoLiquidity, uint256 burn, uint256 marketing, uint256 development)",
  "function reflectionLocked() view returns (bool)",
  "function autoLiquidityLocked() view returns (bool)",
  "function burnLocked() view returns (bool)",
  "function marketingLocked() view returns (bool)",
  "function developmentLocked() view returns (bool)",
  
  // Wallet Limits
  "function maxWalletAmount() view returns (uint256)",
  "function maxBuyAmount() view returns (uint256)",
  "function maxSellAmount() view returns (uint256)",
  "function maxWalletLocked() view returns (bool)",
  "function maxBuyLocked() view returns (bool)",
  "function maxSellLocked() view returns (bool)",
  
  // Trading Delay
  "function tradingDelay() view returns (uint256)",
  "function tradingDelayLocked() view returns (bool)",
  "function contractLocked() view returns (bool)"
];

const WALLET_ABI = [
  "function marketingWallets(uint256) view returns (address)",
  "function developmentWallets(uint256) view returns (address)",
  "function marketingWalletsLocked() view returns (bool)",
  "function developmentWalletsLocked() view returns (bool)"
];

const FEATURE_ABI = [
  ...TAX_ABI,
  ...WALLET_ABI
];

// Contract addresses
const NETWORKS = {
  BSC_MAINNET: {
    chainId: '0x38',
    name: 'BSC',
    nativeSymbol: 'BNB',
    scannerName: 'BSCScan',
    dexName: 'PancakeSwap',
    pancakeUrl: 'https://pancakeswap.finance/info/pairs',
    explorerUrl: 'https://bscscan.com'
  },
  BSC_TESTNET: {
    chainId: '0x61',
    name: 'BSC',
    nativeSymbol: 'BNB',
    scannerName: 'BSCScan',
    dexName: 'PancakeSwap',
    pancakeUrl: 'https://pancakeswap.finance/info/pairs',
    explorerUrl: 'https://testnet.bscscan.com'
  },
  ETHEREUM: {
    chainId: '0x1',
    name: 'Ethereum',
    nativeSymbol: 'ETH',
    scannerName: 'Etherscan',
    dexName: 'Uniswap',
    pancakeUrl: 'https://app.uniswap.org/#/pools',
    explorerUrl: 'https://etherscan.io'
  },
  SEPOLIA: {
    chainId: '0xaa36a7',
    name: 'Sepolia',
    nativeSymbol: 'ETH',
    scannerName: 'Etherscan',
    dexName: 'Uniswap',
    pancakeUrl: 'https://app.uniswap.org/#/pools',
    explorerUrl: 'https://sepolia.etherscan.io'
  },
  AVALANCHE: {
    chainId: '0xa86a',
    name: 'Avalanche',
    nativeSymbol: 'AVAX',
    scannerName: 'Snowtrace',
    dexName: 'Trader Joe',
    pancakeUrl: 'https://traderjoexyz.com/pool',
    explorerUrl: 'https://snowtrace.io'
  },
  AVALANCHE_FUJI: {
    chainId: '0xa869',
    name: 'Avalanche',
    nativeSymbol: 'AVAX',
    scannerName: 'Snowtrace',
    dexName: 'Trader Joe',
    pancakeUrl: 'https://traderjoexyz.com/avalanche/pool',
    explorerUrl: 'https://testnet.snowtrace.io'
  }
};

const FEATURE_DEFINITIONS = [
  {
    id: "buyTax",
    name: "Buy Tax Rate",
    description: "Token buy tax rate",
    icon: <DollarSign className="w-4 h-4" style={{ color: 'var(--color-primary, #10b981)' }} />
  },
  {
    id: "sellTax",
    name: "Sell Tax Rate",
    description: "Token sell tax rate",
    icon: <DollarSign className="w-4 h-4" style={{ color: 'var(--color-secondary, #ef4444)' }} />
  },
  {
    id: "reflectionDistribution",
    name: "Reflection Distribution",
    description: "Reflection distribution percentage",
    icon: <RefreshCcw className="w-4 h-4" style={{ color: 'var(--color-header-accent, #3b82f6)' }} />
  },
  {
    id: "autoLiquidityDistribution",
    name: "Auto-Liquidity Distribution",
    description: "Auto-liquidity distribution percentage",
    icon: <RefreshCcw className="w-4 h-4 text-gray-500" />
  },
  {
    id: "burnDistribution",
    name: "Burn Distribution",
    description: "Burn distribution percentage",
    icon: <Percent className="w-4 h-4" style={{ color: 'var(--color-primary, #f97316)' }} />
  },
  {
    id: "marketingDistribution",
    name: "Marketing Distribution",
    description: "Marketing wallet distribution percentage",
    icon: <Wallet className="w-4 h-4" style={{ color: 'var(--color-secondary, #eab308)' }} />
  },
  {
    id: "developmentDistribution",
    name: "Development Distribution",
    description: "Development wallet distribution percentage",
    icon: <FileCode className="w-4 h-4" style={{ color: 'var(--color-primary, #a855f7)' }} />
  },
  {
    id: "maxWallet",
    name: "Max Wallet Amount",
    description: "Maximum tokens per wallet (Anti-Whale)",
    icon: <Wallet className="w-4 h-4" style={{ color: 'var(--color-header-accent, #3b82f6)' }} />
  },
  {
    id: "maxBuy",
    name: "Max Buy Amount",
    description: "Maximum buy transaction amount (Anti-Pump)",
    icon: <ArrowUpCircle className="w-4 h-4" style={{ color: 'var(--color-primary, #10b981)' }} />
  },
  {
    id: "maxSell",
    name: "Max Sell Amount",
    description: "Maximum sell transaction amount (Anti-Dump)",
    icon: <ArrowDownCircle className="w-4 h-4" style={{ color: 'var(--color-secondary, #ef4444)' }} />
  },
  {
    id: "tradingDelay",
    name: "Trading Delay",
    description: "Delay before trading can begin",
    icon: <Clock className="w-4 h-4" style={{ color: 'var(--color-primary, #a855f7)' }} />
  }
];

const MdbMatrixContractScan: React.FC = () => {
  const { address: userAddress, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();
  const { toast } = useToast();
  const { settings } = useContractScannerSettings();
  const { settings: feeSettings, loading: feeLoading } = useFirebaseFees();
  const { chain } = useNetwork();
  const networkName = chain?.name || 'BSC';
  const networkDisplayName = chain?.name?.replace(' Mainnet', '').replace(' Testnet', '') || 'BSC';
  const pageTitle = `${networkDisplayName} Matrix Contract Scanner`;
  const placeholderText = `${networkDisplayName} Contract Address`;
  const [contractAddress, setContractAddress] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [tokenInfo, setTokenInfo] = useState({ name: '', symbol: '', totalSupply: null, decimals: 18 });
  const [contractType, setContractType] = useState({ isOwnable: false });
  const [debugLoaded, setDebugLoaded] = useState(false);
  const [matrixExpanded, setMatrixExpanded] = useState(false);
  const [showWarningFlash, setShowWarningFlash] = useState(false);
  const [featureStatus, setFeatureStatus] = useState<Record<string, any>>({
    pancakePair: null,
    pancakeRouter: null,
    tradingEnabled: false,
    permanentTradingEnabled: false,
    buyTax: null,
    sellTax: null,
    reflectionDistribution: null,
    autoLiquidityDistribution: null,
    burnDistribution: null,
    marketingDistribution: null,
    developmentDistribution: null,
    maxWallet: null,
    maxBuy: null,
    maxSell: null,
    tradingDelay: null
  });
  const [isVerified, setIsVerified] = useState(false);
  const [matrixKey, setMatrixKey] = useState<number>(0);
  const [currentNetwork, setCurrentNetwork] = useState(NETWORKS.SEPOLIA);
  const [contract, setContract] = useState(null);
  const [walletInfo, setWalletInfo] = useState({
    marketing: { address: '', isLocked: false },
    development: { address: '', isLocked: false }
  });
  const [isMounted, setIsMounted] = useState(false);
  const [masterLocked, setMasterLocked] = useState(false);
  const [isWhitelisted, setIsWhitelisted] = useState(false);
  const [ownerAddress, setOwnerAddress] = useState<string | null>(null);
  const [isOwnershipRenounced, setIsOwnershipRenounced] = useState(false);
  const [matrixResetTrigger, setMatrixResetTrigger] = useState(0);
  const { theme } = useTheme();

  const TransparentShield = ({ className }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );

  // Connect wallet
  const connectWallet = async () => {
    if (typeof window.ethereum === 'undefined') {
      toast({
        title: "Error",
        description: "Please install MetaMask to use this feature",
        variant: "destructive"
      });
      return;
    }

    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      await provider.send("eth_requestAccounts", []);
      const signer = provider.getSigner();
      const address = await signer.getAddress();
      setUserAddress(address);
      setIsConnected(true);
    } catch (error) {
      console.error("Error connecting wallet:", error);
      toast({
        title: "Error",
        description: "Failed to connect wallet",
        variant: "destructive"
      });
    }
  };

  // Effect to check wallet connection on mount
  useEffect(() => {
    const checkConnection = async () => {
      if (typeof window.ethereum !== 'undefined') {
        try {
          const provider = new ethers.providers.Web3Provider(window.ethereum);
          const accounts = await provider.listAccounts();
          if (accounts.length > 0) {
            setUserAddress(accounts[0]);
            setIsConnected(true);
          }
        } catch (error) {
          console.error("Error checking wallet connection:", error);
        }
      }
    };

    checkConnection();

    // Listen for account changes
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', (accounts) => {
        if (accounts.length > 0) {
          setUserAddress(accounts[0]);
          setIsConnected(true);
        } else {
          setUserAddress('');
          setIsConnected(false);
        }
      });
    }
  }, []);

  // Effect to detect network changes
  useEffect(() => {
    const detectNetwork = async () => {
      if (typeof window.ethereum !== 'undefined') {
        try {
          const chainId = await window.ethereum.request({ method: 'eth_chainId' });
          switch (chainId) {
            case NETWORKS.BSC_MAINNET.chainId:
              setCurrentNetwork(NETWORKS.BSC_MAINNET);
              break;
            case NETWORKS.BSC_TESTNET.chainId:
              setCurrentNetwork(NETWORKS.BSC_TESTNET);
              break;
            case NETWORKS.ETHEREUM.chainId:
              setCurrentNetwork(NETWORKS.ETHEREUM);
              break;
            case NETWORKS.SEPOLIA.chainId:
              setCurrentNetwork(NETWORKS.SEPOLIA);
              break;
            case NETWORKS.AVALANCHE.chainId:
              setCurrentNetwork(NETWORKS.AVALANCHE);
              break;
            case NETWORKS.AVALANCHE_FUJI.chainId:
              setCurrentNetwork(NETWORKS.AVALANCHE_FUJI);
              break;
            default:
              console.warn('Unsupported network detected:', chainId);
          }
        } catch (error) {
          console.error("Error detecting network:", error);
        }
      }
    };

    detectNetwork();

    if (window.ethereum) {
      window.ethereum.on('chainChanged', detectNetwork);
      return () => {
        window.ethereum.removeListener('chainChanged', detectNetwork);
      };
    }
  }, []);

  // Helper function to try multiple function variations
  const tryMultipleFunctions = async (contract, functionNames, defaultValue = null) => {
    for (const name of functionNames) {
      try {
        if (contract[name]) {
          const result = await contract[name]();
          return result;
        }
      } catch (error) {
        console.log(`Failed to call ${name}:`, error);
      }
    }
    return defaultValue;
  };

  // Helper function to get tax distribution
  const getTaxDistribution = async (contract) => {
    try {
      // Try individual function calls first
      const reflection = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.REFLECTION_DISTRIBUTION) || "0";
      const autoLiquidity = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.LIQUIDITY_DISTRIBUTION) || "0";
      const burn = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.BURN_DISTRIBUTION) || "0";
      const marketing = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.MARKETING_DISTRIBUTION) || "0";
      const development = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.DEVELOPMENT_DISTRIBUTION) || "0";

      // If any value is found, return the distribution
      if (reflection !== "0" || autoLiquidity !== "0" || burn !== "0" || marketing !== "0" || development !== "0") {
        return {
          reflection: reflection.toString(),
          autoLiquidity: autoLiquidity.toString(),
          burn: burn.toString(),
          marketing: marketing.toString(),
          development: development.toString()
        };
      }

      // Try accessing the struct directly as backup
      const distribution = await contract.taxDistribution();
      return {
        reflection: distribution.reflection?.toString() || "0",
        autoLiquidity: distribution.autoLiquidity?.toString() || "0",
        burn: distribution.burn?.toString() || "0",
        marketing: distribution.marketing?.toString() || "0",
        development: distribution.development?.toString() || "0"
      };
    } catch (error) {
      console.error("Failed to get tax distribution:", error);
      try {
        // Try backup function
        const distribution = await contract._taxDistribution();
        return {
          reflection: distribution.reflection?.toString() || "0",
          autoLiquidity: distribution.autoLiquidity?.toString() || "0",
          burn: distribution.burn?.toString() || "0",
          marketing: distribution.marketing?.toString() || "0",
          development: distribution.development?.toString() || "0"
        };
      } catch (e) {
        console.error("Failed to get backup tax distribution:", e);
        // Return zeros if nothing is found
        return {
          reflection: "0",
          autoLiquidity: "0",
          burn: "0",
          marketing: "0",
          development: "0"
        };
      }
    }
  };

  // Helper function to format values based on feature type
  const formatValue = (value: string, featureId: string) => {
    if (!value || value === "N/A") return "Not Set";
    
    // For tax and distribution features
    if (featureId.includes('Tax') || featureId.includes('Distribution')) {
      return `${Number(value).toFixed(2)}%`;
    }
    
    // For max amounts, calculate percentage of total supply
    if (featureId === 'maxWallet' || featureId === 'maxBuy' || featureId === 'maxSell') {
      try {
        // Check if it's the max possible uint256 value (unlimited)
        const maxUint256 = "115792089237316195423570985008687907853269984665640564039457584007913129639935";
        if (value === maxUint256) {
          return "Unlimited";
        }
        
        // Convert both numbers to BigInt for comparison
        const amount = ethers.BigNumber.from(value);
        const total = tokenInfo.totalSupply;
        
        if (!total) return "N/A";
        
        // Calculate percentage
        const percentage = amount.mul(10000).div(total).toNumber() / 100;
        return `${percentage.toFixed(2)}% (${ethers.utils.formatUnits(amount, tokenInfo.decimals)} tokens)`;
      } catch (error) {
        console.error(`Error formatting ${featureId}:`, error);
        return "Error";
      }
    }
    
    // For trading delay
    if (featureId === 'tradingDelay') {
      const seconds = parseInt(value);
      if (seconds === 0) return "No Delay";
      if (seconds < 60) return `${seconds} seconds`;
      if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes`;
      return `${Math.floor(seconds / 3600)} hours`;
    }
    
    return value;
  };

  // Helper function to detect feature values
  const detectFeature = async (contract, feature) => {
    try {
      let value = null;
      let exists = false;
      let isLocked = false;

      // Try different function name variations
      const variations = FUNCTION_VARIATIONS[feature.id.toUpperCase()] || [
        feature.id,
        `get${feature.id.charAt(0).toUpperCase() + feature.id.slice(1)}`,
        `_${feature.id}`,
        feature.id.toLowerCase(),
        feature.id.toUpperCase()
      ];

      value = await tryMultipleFunctions(contract, variations);
      exists = value !== null && value !== "0";

      // Check if the feature is locked
      const lockFunctionNames = [
        `${feature.id}Locked`,
        `is${feature.id}Locked`,
        `${feature.id}IsLocked`,
        `_${feature.id}Locked`
      ];

      const lockStatus = await tryMultipleFunctions(contract, lockFunctionNames);
      isLocked = lockStatus === true;

      // Format the value based on feature type
      if (exists && value !== null) {
        // For max amounts, store the raw value and let formatValue handle the percentage conversion
        if (feature.id.includes('maxWallet') || feature.id.includes('maxBuy') || feature.id.includes('maxSell')) {
          value = value.toString();
        } else if (feature.id.includes('Tax') || feature.id.includes('Distribution')) {
          value = ethers.utils.formatUnits(value, 2);
        } else if (feature.id === 'tradingDelay') {
          value = value.toString();
        }
      }

      return {
        exists,
        value: value?.toString() || "N/A",
        isLocked
      };
    } catch (error) {
      console.error(`Error detecting feature ${feature.id}:`, error);
      return {
        exists: false,
        value: "Error",
        isLocked: false
      };
    }
  };

  // Add helper function for formatting values
  const truncateAddress = (address: string) => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatTokenAmount = (amount: any, decimals: number) => {
    return ethers.utils.formatUnits(amount, decimals);
  };

  // Add contract verification check
  const checkContractVerification = async (address: string) => {
    try {
      if (!walletClient) return false;
      
      const provider = new ethers.providers.Web3Provider(walletClient.transport);
      const contract = new ethers.Contract(address, FEATURE_ABI, provider);
      
      // Try to call basic functions that should exist in a verified contract
      await Promise.all([
        contract.name(),
        contract.symbol(),
        contract.decimals(),
        contract.totalSupply()
      ]);
      
      // If we can successfully call these functions, consider the contract verified
      return true;
    } catch (error) {
      console.error("Contract verification check failed:", error);
      return false;
    }
  };

  // Helper function to detect DEX information
  const getDexAddresses = async (contract: ethers.Contract) => {
    try {
      let pair = null;
      let router = null;

      // Try PancakeSwap
      try {
        pair = await contract.pancakePair();
        router = await contract.pancakeRouter();
      } catch {}

      // Try Uniswap
      if (!pair || !router) {
        try {
          pair = await contract.uniswapPair();
          router = await contract.uniswapRouter();
        } catch {}
      }

      // Try TraderJoe
      if (!pair || !router) {
        try {
          pair = await contract.traderJoePair();
          router = await contract.traderJoeRouter();
        } catch {}
      }

      return { pair, router };
    } catch (error) {
      console.error('Error getting DEX addresses:', error);
      return { pair: null, router: null };
    }
  };

  // Function to get current wallets
  const getCurrentWallets = async (contract: any, type: 'marketing' | 'development') => {
    try {
      const wallets: string[] = [];
      let index = 0;
      const funcName = `${type}Wallets`;
      
      while (true) {
        try {
          const wallet = await contract[funcName](index);
          if (!wallet || wallet === '0x0000000000000000000000000000000000000000') {
            break;
          }
          wallets.push(wallet);
          index++;
        } catch (error) {
          console.error(`Error in ${type}Wallets(${index}):`, error);
          break;
        }
      }
      
      return wallets[0] || '';
    } catch (error) {
      console.error(`Error getting ${type} wallets:`, error);
      return '';
    }
  };

  // Function to check if wallet is locked
  const checkWalletLocked = async (contract: any, type: 'marketing' | 'development') => {
    try {
      const funcName = `${type}WalletsLocked`;
      const result = await contract[funcName]();
      return result;
    } catch (error) {
      console.error(`Error checking ${type} wallet lock status:`, error);
      return false;
    }
  };

  // Function to fetch wallet information
  const fetchWalletInfo = async (contract: any) => {
    if (!contract) return;

    try {
      console.log('Fetching wallet info...');
      console.log('Contract methods:', Object.keys(contract).filter(key => typeof contract[key] === 'function'));

      const [marketingAddress, developmentAddress] = await Promise.all([
        getCurrentWallets(contract, 'marketing'),
        getCurrentWallets(contract, 'development')
      ]);

      console.log('Marketing address:', marketingAddress);
      console.log('Development address:', developmentAddress);

      const [marketingLocked, developmentLocked] = await Promise.all([
        checkWalletLocked(contract, 'marketing'),
        checkWalletLocked(contract, 'development')
      ]);

      console.log('Marketing locked:', marketingLocked);
      console.log('Development locked:', developmentLocked);

      setWalletInfo({
        marketing: { address: marketingAddress, isLocked: marketingLocked },
        development: { address: developmentAddress, isLocked: developmentLocked }
      });
    } catch (error) {
      console.error('Error fetching wallet information:', error);
    }
  };

  // Add wallet info fetch to useEffect
  useEffect(() => {
    if (contract) {
      fetchWalletInfo(contract);
    }
  }, [contract]);

  // Add master lock detection
  useEffect(() => {
    const checkMasterLock = async () => {
      if (contract) {
        try {
          console.log('Checking master lock status...');
          const locked = await contract.contractLocked();
          console.log('Master lock status:', locked);
          setMasterLocked(!!locked);
        } catch (error) {
          console.error('Error checking master lock:', error);
          setMasterLocked(false);
        }
      }
    };
    checkMasterLock();
  }, [contract]);

  // Add owner check
  useEffect(() => {
    const checkContractOwner = async () => {
      if (!contract) return;

      try {
        console.log("Checking contract owner...");
        const owner = await contract.owner();
        console.log("Contract owner:", owner);
        setOwnerAddress(owner);
        
        // Check if ownership is renounced (owner address is zero address)
        const isRenounced = owner === "0x0000000000000000000000000000000000000000";
        setIsOwnershipRenounced(isRenounced);
        
      } catch (err) {
        console.error("Error checking contract owner:", err);
        setOwnerAddress(null);
        setIsOwnershipRenounced(false);
      }
    };
    checkContractOwner();
  }, [contract]);

  // Update the validateContract function
  const validateContract = async () => {
    if (!isConnected) {
      toast({
        title: "Error",
        description: "Please connect your wallet first",
        variant: "destructive"
      });
      return;
    }

    if (!ethers.utils.isAddress(contractAddress)) {
      toast({
        title: "Error",
        description: "Please enter a valid contract address",
        variant: "destructive"
      });
      return;
    }

    if (!feeSettings?.networkFees) {
      toast({
        title: "Error",
        description: "Fee settings not loaded yet. Please try again.",
        variant: "destructive"
      });
      return;
    }

    // Get the current chain ID
    const provider = new ethers.providers.Web3Provider(walletClient.transport);
    const network = await provider.getNetwork();
    const chainId = network.chainId;

    // Map testnet and L2 chain ID to mainnet for fee lookup
    const mainnetChainId = {
      // Ethereum family (use ETH fees)
      1: 1,        // Mainnet
      5: 1,        // Goerli
      11155111: 1, // Sepolia
      11124: 1,    // Abstract Testnet
      2741: 1,     // Abstract Mainnet
      8453: 1,     // Base Mainnet
      84532: 1,    // Base Sepolia
      10: 1,       // OP Mainnet
      11155420: 1, // OP Sepolia
      42161: 1,    // Arbitrum One
      421614: 1,   // Arbitrum Sepolia

      // BSC
      56: 56,      // Mainnet
      97: 56,      // Testnet

      // Avalanche
      43114: 43114, // Mainnet
      43113: 43114  // Fuji Testnet
    }[chainId] || chainId;

    // Get network-specific fees using mainnet chain ID
    const networkFees = feeSettings.networkFees[mainnetChainId];
    if (!networkFees) {
      toast({
        title: "Error",
        description: "Fees not configured for this network. Please switch to a supported network.",
        variant: "destructive"
      });
      return;
    }

    // Trigger matrix reset
    setMatrixResetTrigger(prev => prev + 1);
    setIsValidating(true);
    setShowWarningFlash(false);

    try {
      const signer = provider.getSigner();
      
      // Get fee amount and collector from Firebase settings
      const platformFeeAmount = ethers.utils.parseEther(networkFees.scanFee);
      const feeCollectorAddress = feeSettings.collectors[0].address;
      
      console.log('Sending platform fee transaction...');
      console.log('Fee collector:', feeCollectorAddress);
      console.log('Fee amount:', platformFeeAmount.toString());

      // Send the platform fee
      const tx = await signer.sendTransaction({
        to: feeCollectorAddress,
        value: platformFeeAmount
      });

      // Wait for the transaction to be mined
      await tx.wait();
      console.log('Platform fee sent successfully');

      const isVerifiedResult = await checkContractVerification(contractAddress);
      setIsVerified(isVerifiedResult);

      if (!isVerifiedResult) {
        toast({
          title: "Warning",
          description: "Contract might not be fully verified. Some features may not be detected.",
          variant: "warning"
        });
        setIsValidating(false);
        return;
      }

      const contract = new ethers.Contract(contractAddress, FEATURE_ABI, provider);
      setContract(contract);

      // Get basic token info
      try {
        const [name, symbol, totalSupply, decimals] = await Promise.all([
          contract.name(),
          contract.symbol(),
          contract.totalSupply(),
          contract.decimals()
        ]);

        setTokenInfo({
          name,
          symbol,
          totalSupply,
          decimals
        });

        // Get distribution features
        const distribution = await getTaxDistribution(contract);
        const { pair, router } = await getDexAddresses(contract);
        const pancakeInfo = {
          pancakePair: pair,
          pancakeRouter: router,
          tradingEnabled: await contract.tradingEnabled(),
          permanentTradingEnabled: await contract.permanentTradingEnabled(),
          tradingEnabledLocked: await contract.tradingEnabledLocked()
        };
        const features: Record<string, any> = {
          pancakePair: pancakeInfo.pancakePair,
          pancakeRouter: pancakeInfo.pancakeRouter,
          tradingEnabled: pancakeInfo.tradingEnabled,
          permanentTradingEnabled: pancakeInfo.permanentTradingEnabled,
          tradingEnabledLocked: pancakeInfo.tradingEnabledLocked
        };

        if (distribution) {
          features.reflectionDistribution = {
            exists: true,
            value: distribution.reflection,
            isLocked: await tryMultipleFunctions(contract, ['reflectionLocked']) || false
          };
          
          features.autoLiquidityDistribution = {
            exists: true,
            value: distribution.autoLiquidity,
            isLocked: await tryMultipleFunctions(contract, ['autoLiquidityLocked']) || false
          };
          
          features.burnDistribution = {
            exists: true,
            value: distribution.burn,
            isLocked: await tryMultipleFunctions(contract, ['burnLocked']) || false
          };
          
          features.marketingDistribution = {
            exists: true,
            value: distribution.marketing,
            isLocked: await tryMultipleFunctions(contract, ['marketingLocked']) || false
          };
          
          features.developmentDistribution = {
            exists: true,
            value: distribution.development,
            isLocked: await tryMultipleFunctions(contract, ['developmentLocked']) || false
          };
        }

        // Get tax features
        try {
          const buyTax = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.BUY_TAX);
          if (buyTax) {
            features.buyTax = {
              exists: true,
              value: buyTax.toString(),
              isLocked: await tryMultipleFunctions(contract, ['buyTaxLocked']) || false
            };
          }
        } catch (error) {
          console.error("Failed to get buy tax:", error);
        }

        try {
          const sellTax = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.SELL_TAX);
          if (sellTax) {
            features.sellTax = {
              exists: true,
              value: sellTax.toString(),
              isLocked: await tryMultipleFunctions(contract, ['sellTaxLocked']) || false
            };
          }
        } catch (error) {
          console.error("Failed to get sell tax:", error);
        }

        // Get limit features
        try {
          const maxWallet = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.MAX_WALLET);
          if (maxWallet) {
            features.maxWallet = {
              exists: true,
              value: maxWallet.toString(),
              isLocked: await tryMultipleFunctions(contract, ['maxWalletLocked']) || false
            };
          }
        } catch (error) {
          console.error("Failed to get max wallet:", error);
        }

        try {
          const maxBuy = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.MAX_BUY);
          if (maxBuy) {
            features.maxBuy = {
              exists: true,
              value: maxBuy.toString(),
              isLocked: await tryMultipleFunctions(contract, ['maxBuyLocked']) || false
            };
          }
        } catch (error) {
          console.error("Failed to get max buy:", error);
        }

        try {
          const maxSell = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.MAX_SELL);
          if (maxSell) {
            features.maxSell = {
              exists: true,
              value: maxSell.toString(),
              isLocked: await tryMultipleFunctions(contract, ['maxSellLocked']) || false
            };
          }
        } catch (error) {
          console.error("Failed to get max sell:", error);
        }

        try {
          const tradingDelay = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.TRADING_DELAY);
          if (tradingDelay) {
            features.tradingDelay = {
              exists: true,
              value: tradingDelay.toString(),
              isLocked: await tryMultipleFunctions(contract, ['tradingDelayLocked']) || false
            };
          }
        } catch (error) {
          console.error("Failed to get trading delay:", error);
        }

        setFeatureStatus(features);
        setDebugLoaded(true);

        // Fetch wallet info after other features are loaded
        await fetchWalletInfo(contract);

        toast({
          title: "Success",
          description: "Contract scanned successfully",
        });

      } catch (error) {
        console.error("Error getting token info:", error);
        toast({
          title: "Error",
          description: "Failed to read token information. This might not be a valid token contract.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error validating contract:", error);
      toast({
        title: "Error",
        description: "Failed to validate contract. Please check the address and try again.",
        variant: "destructive"
      });
    } finally {
      setIsValidating(false);
    }
  };

  useEffect(() => {
    if (!tokenInfo.name) return;
    
    setDebugLoaded(false);
    setMatrixExpanded(false);
    setShowWarningFlash(false);
    
    const loadTimer = setTimeout(() => {
      setDebugLoaded(true);
      setTimeout(() => setShowWarningFlash(false), 500);
    }, 1000);

    const expandTimer = setTimeout(() => setMatrixExpanded(true), 3500);

    return () => {
      clearTimeout(loadTimer);
      clearTimeout(expandTimer);
    };
  }, [tokenInfo.name]);

  // Add network fee display helper
  const getNetworkFeeDisplay = () => {
    if (!feeSettings?.networkFees) return "Fee settings not loaded";
    
    // Get current network
    const chainId = walletClient?.chain?.id;
    if (!chainId) return "Connect wallet to view fee";

    // Map testnet and L2 chain IDs to their mainnet counterparts for fee lookup
    const mainnetChainId = {
      // Ethereum family (use ETH fees)
      1: 1,        // Mainnet
      5: 1,        // Goerli
      11155111: 1, // Sepolia
      11124: 1,    // Abstract Testnet
      2741: 1,     // Abstract Mainnet
      8453: 1,     // Base Mainnet
      84532: 1,    // Base Sepolia
      10: 1,       // OP Mainnet
      11155420: 1, // OP Sepolia
      42161: 1,    // Arbitrum One
      421614: 1,   // Arbitrum Sepolia

      // BSC
      56: 56,      // Mainnet
      97: 56,      // Testnet

      // Avalanche
      43114: 43114, // Mainnet
      43113: 43114  // Fuji Testnet
    }[chainId] || chainId;

    const networkFees = feeSettings.networkFees[mainnetChainId];
    if (!networkFees) return "Fee not configured for this network";

    const networkSymbols = {
      // Ethereum family
      1: "ETH",        // Mainnet
      5: "ETH",        // Goerli
      11155111: "ETH", // Sepolia
      11124: "ETH",    // Abstract Testnet
      2741: "ETH",     // Abstract Mainnet
      8453: "ETH",     // Base Mainnet
      84532: "ETH",    // Base Sepolia
      10: "ETH",       // OP Mainnet
      11155420: "ETH", // OP Sepolia
      42161: "ETH",    // Arbitrum One
      421614: "ETH",   // Arbitrum Sepolia

      // BSC
      56: "BNB",      // Mainnet
      97: "BNB",      // Testnet

      // Avalanche
      43114: "AVAX",   // Mainnet
      43113: "AVAX"    // Fuji Testnet
    };

    const symbol = networkSymbols[chainId] || "";
    return `Scan Fee: ${networkFees.scanFee} ${symbol}`;
  };

  const MasterLockStatus = ({ contract }) => {
    return (
      <Card className={`h-full bg-black/30 backdrop-blur-sm ${masterLocked ? 'border-2 border-[var(--color-primary)]' : 'border-2 border-[var(--color-secondary)]'}`}>
        <div className="flex flex-col items-center justify-center h-full py-6">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl font-bold text-center mb-6">
              Master Lock Status
            </CardTitle>
            <div className="flex flex-col items-center justify-center">
              {masterLocked ? (
                <TransparentShield className="w-32 h-32 text-[var(--color-primary)]" />
              ) : (
                <>
                  <TransparentShield className="w-32 h-32 text-[var(--color-secondary)]" />
                  <p className="text-lg text-[var(--color-secondary)] mt-2">No Master Lock</p>
                </>
              )}
            </div>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center">
            <div className={`text-xl font-medium text-center ${masterLocked ? 'text-[var(--color-primary)]' : 'text-[var(--color-secondary)]'}`}>
              {masterLocked ? (
                <>
                  <p className="mb-4">Contract is Permanently Locked</p>
                  <p className="text-lg mb-3">All contract modifications are permanently disabled</p>
                  <p className="text-lg">Even the owner cannot modify the contract</p>
                </>
              ) : (
                <p className="mb-4">Contract Modifications are Currently Enabled</p>
              )}
            </div>
          </CardContent>
        </div>
      </Card>
    );
  };

  const OwnershipStatus = () => {
    const isRenounced = ownerAddress === "0x0000000000000000000000000000000000000000";
    
    return (
      <Card className={`h-full bg-black/30 backdrop-blur-sm ${isRenounced ? 'border-2 border-[var(--color-primary)]' : 'border-2 border-[var(--color-secondary)]'}`}>
        <div className="flex flex-col items-center justify-center h-full py-6">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl font-bold text-center mb-6">
              Ownership Status
            </CardTitle>
            <div className="flex justify-center mb-8">
              {isRenounced ? (
                <TransparentShield className="w-32 h-32 text-[var(--color-primary)]" />
              ) : (
                <TransparentShield className="w-32 h-32 text-[var(--color-secondary)]" />
              )}
            </div>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center">
            <div className={`text-xl font-medium text-center ${isRenounced ? 'text-[var(--color-primary)]' : 'text-[var(--color-secondary)]'}`}>
              {isRenounced ? (
                <>
                  <p className="mb-4">Ownership Renounced</p>
                  <p className="text-lg mb-3">Contract ownership has been permanently renounced</p>
                  <p className="text-sm font-mono">{ownerAddress}</p>
                </>
              ) : (
                <>
                  <p className="mb-4">Not Renounced</p>
                  <p className="text-lg mb-3">Current Owner:</p>
                  <p className="text-sm font-mono">{ownerAddress}</p>
                </>
              )}
            </div>
          </CardContent>
        </div>
      </Card>
    );
  };

  const HoneyPotDetector = () => {
    // Trading is effectively permanent if it's permanently enabled, or if trading is enabled and either locked or contract is locked/renounced
    const isEffectivelyPermanent = featureStatus.permanentTradingEnabled || 
      (featureStatus.tradingEnabled && (featureStatus.tradingEnabledLocked || masterLocked || isOwnershipRenounced));

    return (
      <Card className="bg-black/30 backdrop-blur-sm border-[var(--color-primary)]/20">
        <CardContent className="p-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-semibold text-white">HoneyPot Detector</h3>
                <p className="text-sm text-gray-400 mt-1">Analysis of contract trading controls</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={isEffectivelyPermanent ? "text-[var(--color-primary)]" : "text-[var(--color-secondary)]"}>
                  {isEffectivelyPermanent ? "No Risk" : "At Risk"}
                </span>
              </div>
            </div>

            {/* Trading Controls Status */}
            <div className="space-y-3 mt-2">
              {/* Trading Status */}
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Trading Status:</span>
                <div className="flex items-center gap-2">
                  <span className={featureStatus.tradingEnabled ? "text-[var(--color-primary)]" : "text-[var(--color-secondary)]"}>
                    {featureStatus.tradingEnabled ? "Enabled" : "Disabled"}
                  </span>
                  <span className="text-gray-500">
                    ({featureStatus.tradingEnabledLocked ? "Locked" : "Unlocked"})
                  </span>
                </div>
              </div>

              {/* Permanent Trading Status */}
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Permanent Trading:</span>
                <div className="flex items-center gap-2">
                  <span className={isEffectivelyPermanent ? "text-[var(--color-primary)]" : "text-[var(--color-secondary)]"}>
                    {isEffectivelyPermanent ? "Enabled" : "Disabled"}
                  </span>
                  {(featureStatus.permanentTradingEnabled || (featureStatus.tradingEnabled && (featureStatus.tradingEnabledLocked || masterLocked || isOwnershipRenounced))) && (
                    <span className="text-gray-500">
                      {featureStatus.permanentTradingEnabled ? "(Due to Contract Lock)" : 
                       featureStatus.tradingEnabledLocked ? "(Due to Trading Status Lock)" :
                       masterLocked ? "(Due to Contract Lock)" : 
                       isOwnershipRenounced ? "(Due to Renounced Ownership)" : ""}
                    </span>
                  )}
                </div>
              </div>

              {/* Contract Lock Status */}
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Contract Lock:</span>
                <span className={masterLocked ? "text-[var(--color-primary)]" : "text-[var(--color-secondary)]"}>
                  {masterLocked ? "Locked" : "Unlocked"}
                </span>
              </div>

              {/* Ownership Status */}
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Ownership:</span>
                <span className={isOwnershipRenounced ? "text-[var(--color-primary)]" : "text-[var(--color-secondary)]"}>
                  {isOwnershipRenounced ? "Renounced" : "Not Renounced"}
                </span>
              </div>
            </div>

            {/* Risk Assessment Message */}
            <div className={`mt-4 p-4 rounded-lg ${isEffectivelyPermanent ? "bg-[var(--color-primary)]/5 border border-[var(--color-primary)]/20" : "bg-orange-500/5 border border-[var(--color-secondary)]/20"}`}>
              <div className="flex items-start gap-2">
                {isEffectivelyPermanent ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 text-[var(--color-primary)] mt-0.5" />
                    <div>
                      <p className="text-[var(--color-primary)] font-medium">Safe: No HoneyPot Risk Detected</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Trading cannot be disabled due to one or more of the following: Trading effectively permanent • Contract locked • Ownership renounced
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-5 h-5 text-[var(--color-secondary)] mt-0.5" />
                    <div>
                      <p className="text-[var(--color-secondary)] font-medium">Warning: Potential HoneyPot Risk</p>
                      <p className="text-sm text-gray-400 mt-1">
                        The contract owner has the ability to disable trading. Exercise caution and ensure you trust the contract owner.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Add download function
  const downloadScanResults = () => {
    // Use the exact same values as displayed in the UI
    const isEffectivelyPermanent = featureStatus.permanentTradingEnabled || 
      (featureStatus.tradingEnabled && (featureStatus.tradingEnabledLocked || masterLocked || isOwnershipRenounced));

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>MdbApp Matrix Scan Results</title>
          <style>
            body {
              font-family: system-ui, -apple-system, sans-serif;
              background: #000;
              color: #fff;
              line-height: 1.5;
              padding: 2rem;
              margin: 0;
            }
            .header {
              text-align: center;
              margin-bottom: 2rem;
              padding: 2rem;
              background: rgba(0, 0, 0, 0.6);
              border: 1px solid rgba(0, 255, 0, 0.2);
              border-radius: 0.5rem;
            }
            .header h1 {
              color: #00ff00;
              margin-bottom: 1rem;
            }
            .header p {
              color: #888;
              margin: 0.5rem 0;
            }
            .logo {
              max-width: 200px;
              margin-bottom: 1rem;
            }
            .card {
              background: rgba(0, 0, 0, 0.4);
              border: 1px solid rgba(0, 255, 0, 0.2);
              border-radius: 0.5rem;
              padding: 1.5rem;
              margin-bottom: 1.5rem;
            }
            .card-title {
              font-size: 1.25rem;
              font-weight: 600;
              margin-bottom: 1rem;
              padding-bottom: 0.5rem;
              border-bottom: 1px solid rgba(0, 255, 0, 0.2);
            }
            .feature-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 0.5rem 0;
              border-bottom: 1px solid rgba(0, 255, 0, 0.1);
            }
            .feature-name { 
              color: #888;
              display: flex;
              flex-direction: column;
            }
            .feature-description {
              font-size: 0.875rem;
              opacity: 0.7;
            }
            .feature-value { color: #00ff00; }
            .feature-value.warning { color: #f59e0b; }
            .status-dot {
              display: inline-block;
              width: 8px;
              height: 8px;
              border-radius: 50%;
              margin-right: 0.5rem;
            }
            .status-dot.enabled { background: #22c55e; }
            .status-dot.disabled { background: #f59e0b; }
            .status-text { margin-left: 0.5rem; color: #888; }
          </style>
        </head>
        <body>
          <div class="container">
            <!-- Header -->
            <div class="header">
              <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjUwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgogIDx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjMDBmZjAwIiBmb250LWZhbWlseT0ic3lzdGVtLXVpLCAtYXBwbGUtc3lzdGVtLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjI0cHgiPk1kYkFwcDwvdGV4dD4KPC9zdmc+" alt="MdbApp Logo" class="logo">
              <h1>Matrix Scan Results</h1>
              <p>Contract: ${contractAddress}</p>
              <p>Scan Date: ${new Date().toLocaleString()}</p>
            </div>

            <!-- Token Information -->
            <div class="card">
              <div class="card-title">Token Information</div>
              <div class="feature-row">
                <span class="feature-name">Name:</span>
                <span class="feature-value">${tokenInfo.name}</span>
              </div>
              <div class="feature-row">
                <span class="feature-name">Symbol:</span>
                <span class="feature-value">${tokenInfo.symbol}</span>
              </div>
              <div class="feature-row">
                <span class="feature-name">Total Supply:</span>
                <span class="feature-value">${ethers.utils.formatUnits(tokenInfo.totalSupply, tokenInfo.decimals)}</span>
              </div>
              <div class="feature-row">
                <span class="feature-name">Contract Verification:</span>
                <span class="feature-value">
                  <span class="status-dot ${isVerified ? 'enabled' : 'disabled'}"></span>
                  ${isVerified ? 'Verified' : 'Not Verified'}
                </span>
              </div>
            </div>

            <!-- Trading Information -->
            <div class="card">
              <div class="card-title">Trading Information</div>
              <div class="feature-row">
                <span class="feature-name">Trading Status:</span>
                <div class="feature-value">
                  <span class="status-dot ${featureStatus.tradingEnabled ? 'enabled' : 'disabled'}"></span>
                  ${featureStatus.tradingEnabled ? 'Enabled' : 'Disabled'}
                  <span class="status-text">(${featureStatus.tradingEnabledLocked ? 'Locked' : 'Unlocked'})</span>
                </div>
              </div>
              <div class="feature-row">
                <span class="feature-name">Permanent Trading:</span>
                <div class="feature-value">
                  <span class="status-dot ${isEffectivelyPermanent ? 'enabled' : 'disabled'}"></span>
                  ${isEffectivelyPermanent ? 'Enabled' : 'Disabled'}
                  ${featureStatus.permanentTradingEnabled ? '(Due to Contract Lock)' : 
                    featureStatus.tradingEnabledLocked ? '(Due to Trading Status Lock)' :
                    masterLocked ? '(Due to Contract Lock)' : 
                    isOwnershipRenounced ? '(Due to Renounced Ownership)' : ''}
                </div>
              </div>
            </div>

            <!-- Tax Features -->
            <div class="card">
              <div class="card-title">Tax Features</div>
              <div class="feature-row">
                <span class="feature-name">Buy Tax:</span>
                <div class="feature-value">
                  ${featureStatus.buyTax?.value}
                  <span class="status-text">(${featureStatus.buyTax?.isLocked ? 'Locked' : 'Unlocked'})</span>
                </div>
              </div>
              <div class="feature-row">
                <span class="feature-name">Sell Tax:</span>
                <div class="feature-value">
                  ${featureStatus.sellTax?.value}
                  <span class="status-text">(${featureStatus.sellTax?.isLocked ? 'Locked' : 'Unlocked'})</span>
                </div>
              </div>
            </div>

            <!-- Distribution Features -->
            <div class="card">
              <div class="card-title">Distribution Features</div>
              ${['reflection', 'autoLiquidity', 'burn', 'marketing', 'development'].map(type => `
                <div class="feature-row">
                  <span class="feature-name">${type.charAt(0).toUpperCase() + type.slice(1)} Distribution:</span>
                  <div class="feature-value">
                    ${featureStatus[type + 'Distribution']?.value}
                    <span class="status-text">(${featureStatus[type + 'Distribution']?.isLocked ? 'Locked' : 'Unlocked'})</span>
                  </div>
                </div>
              `).join('')}
            </div>

            <!-- Wallet Information -->
            <div class="card">
              <div class="card-title">Wallet Information</div>
              <div class="feature-row">
                <span class="feature-name">Marketing Wallet:</span>
                <div class="feature-value">
                  ${walletInfo?.marketing?.address}
                  <span class="status-text">(${walletInfo?.marketing?.isLocked ? 'Locked' : 'Unlocked'})</span>
                </div>
              </div>
              <div class="feature-row">
                <span class="feature-name">Development Wallet:</span>
                <div class="feature-value">
                  ${walletInfo?.development?.address}
                  <span class="status-text">(${walletInfo?.development?.isLocked ? 'Locked' : 'Unlocked'})</span>
                </div>
              </div>
            </div>

            <!-- Limits and Security -->
            <div class="card">
              <div class="card-title">Limits and Security</div>
              <div class="feature-row">
                <div class="feature-name">
                  Max Wallet Amount
                  <span class="feature-description">Maximum tokens per wallet (Anti-Whale)</span>
                </div>
                <div class="feature-value">
                  ${formatValue(featureStatus.maxWallet?.value, 'maxWallet')}
                  <span class="status-text">(${featureStatus.maxWallet?.isLocked ? 'Locked' : 'Unlocked'})</span>
                </div>
              </div>
              <div class="feature-row">
                <div class="feature-name">
                  Max Buy Amount
                  <span class="feature-description">Maximum buy transaction amount (Anti-Pump)</span>
                </div>
                <div class="feature-value">
                  ${formatValue(featureStatus.maxBuy?.value, 'maxBuy')}
                  <span class="status-text">(${featureStatus.maxBuy?.isLocked ? 'Locked' : 'Unlocked'})</span>
                </div>
              </div>
              <div class="feature-row">
                <div class="feature-name">
                  Max Sell Amount
                  <span class="feature-description">Maximum sell transaction amount (Anti-Dump)</span>
                </div>
                <div class="feature-value">
                  ${formatValue(featureStatus.maxSell?.value, 'maxSell')}
                  <span class="status-text">(${featureStatus.maxSell?.isLocked ? 'Locked' : 'Unlocked'})</span>
                </div>
              </div>
              <div class="feature-row">
                <div class="feature-name">
                  Trading Delay
                  <span class="feature-description">Delay before trading can begin</span>
                </div>
                <div class="feature-value">
                  ${formatValue(featureStatus.tradingDelay?.value, 'tradingDelay')}
                  <span class="status-text">(${featureStatus.tradingDelay?.isLocked ? 'Locked' : 'Unlocked'})</span>
                </div>
              </div>
            </div>

            <!-- HoneyPot Detector -->
            <div class="card">
              <div class="card-title">HoneyPot Detector</div>
              <div class="feature-row">
                <span class="feature-name">Trading Status:</span>
                <div class="feature-value ${featureStatus.tradingEnabled ? '' : 'warning'}">
                  ${featureStatus.tradingEnabled ? 'Enabled' : 'Disabled'}
                </div>
              </div>
              <div class="feature-row">
                <span class="feature-name">Contract Lock:</span>
                <div class="feature-value ${masterLocked ? '' : 'warning'}">
                  ${masterLocked ? 'Locked' : 'Unlocked'}
                </div>
              </div>
              <div class="feature-row">
                <span class="feature-name">Ownership:</span>
                <div class="feature-value ${isOwnershipRenounced ? '' : 'warning'}">
                  ${isOwnershipRenounced ? 'Renounced' : 'Not Renounced'}
                </div>
              </div>
            </div>

            <!-- Master Lock Status -->
            <div class="card">
              <div class="card-title">Master Lock Status</div>
              <div class="feature-row">
                <div class="feature-value ${masterLocked ? '' : 'warning'}">
                  ${masterLocked ? 'Contract is Permanently Locked' : 'Contract Modifications are Currently Enabled'}
                </div>
              </div>
              ${masterLocked ? `
                <div class="feature-row">
                  <div class="feature-value">All contract modifications are permanently disabled</div>
                </div>
                <div class="feature-row">
                  <div class="feature-value">Even the owner cannot modify the contract</div>
                </div>
              ` : ''}
            </div>

            <!-- Ownership Status -->
            <div class="card">
              <div class="card-title">Ownership Status</div>
              <div class="feature-row">
                <div class="feature-value ${isOwnershipRenounced ? '' : 'warning'}">
                  ${isOwnershipRenounced ? 'Ownership Renounced' : 'Not Renounced'}
                </div>
              </div>
              <div class="feature-row">
                <span class="feature-value">${ownerAddress}</span>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;

    // Create blob and download
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `matrix-scan-${contractAddress}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
      <div className="border-b border-[var(--color-primary)]/30 last:border-b-0">
        <button
          className="w-full py-5 px-4 flex justify-between items-center hover:bg-[var(--color-primary)]/10 transition-colors duration-200"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="text-lg font-medium" style={{ color: 'var(--color-primary)' }}>{question}</span>
          <span className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--color-primary)' }}>
            ▼
          </span>
        </button>
        <div
          className={`overflow-hidden transition-all duration-200 ${
            isOpen ? 'max-h-96 py-4 px-4' : 'max-h-0'
          }`}
        >
          <p className="text-gray-300">{answer}</p>
        </div>
      </div>
    );
  };

  const faqData = [
    {
      question: "What is the MdbApp Matrix Contract Scanner?",
      answer: "The MdbApp Matrix Contract Scanner is a powerful security tool that analyzes token contracts deployed on the blockchain network. It performs a comprehensive scan to verify contract features, identify potential security risks, and validate the implementation of standard functions."
    },
    {
      question: "What does the scanner check for?",
      answer: "The scanner checks for multiple contract features including: ownership functions, tax mechanisms, trading limits (max wallet/buy/sell), blacklist functions, trading controls, and more. It also verifies if these features are properly implemented according to blockchain standards."
    },
    {
      question: "How do I use the Matrix Scanner?",
      answer: "Simply enter your token's contract address in the input field and click 'Scan Contract'. The scanner will automatically analyze the contract and provide a detailed report of all detected features and functions. You'll also get a verification status for each feature."
    },
    {
      question: "What do the verification icons mean?",
      answer: "Green checkmark (✓) means the feature is properly implemented. Yellow warning (⚠) indicates a potential concern or non-standard implementation. Red X (✗) shows the feature is missing or improperly implemented. Blue info (ℹ) provides additional information about the feature."
    },
    {
      question: "Can I download the scan results?",
      answer: "Yes, after the scan is complete, you can download a detailed report of all findings. This report includes all detected features, their implementation status, and any security recommendations. This is useful for documentation or sharing with your community."
    },
    {
      question: "Why should I scan my contract?",
      answer: "Scanning your contract helps ensure it meets security standards and functions as intended. It helps identify any potential vulnerabilities or implementation issues before they become problems. It's also a great way to verify all features are working correctly after deployment."
    }
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      {/* Matrix Animation Background */}
      <div className="fixed inset-0 w-full h-full">
        <MatrixOverlay resetTrigger={matrixResetTrigger} forceClassicGreen={false} />
      </div>

      <div className="container mx-auto p-6 space-y-6 max-w-4xl relative z-10">
        <div className="flex flex-col items-center justify-center mb-8">
          <MatrixLogo 
            className="w-16 h-16 mb-4 animate-spin" 
            style={{ 
              color: 'var(--color-primary)', 
              animationDuration: '3s',
              '--ring-color': 'var(--color-header-accent)'
            } as React.CSSProperties} 
            ringColor="var(--ring-color)"
          />
          <h1 className="text-4xl font-bold text-center mb-2 opacity-100" style={{ color: 'var(--color-primary)' }}>
            {networkDisplayName} Matrix Contract Scanner
          </h1>
          <p className="text-center text-muted-foreground max-w-2xl mb-4 opacity-100">
            Scan and analyze token contracts deployed by the MdbApp.io factory on the {networkDisplayName} network. This tool provides a detailed security analysis and feature verification.
          </p>
        </div>
        <Card className="bg-black/30 backdrop-blur-sm border-[var(--color-primary)]">
          <CardHeader>
            <CardTitle className="text-2xl">Contract Scanner</CardTitle>
            <CardDescription>
              Enter a token contract address to analyze
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <UserTokenSelector
                onSelectToken={(address) => setContractAddress(address)}
                selectedToken={contractAddress}
                label="Quick Select Your Token"
              />
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Input
                    value={contractAddress}
                    onChange={(e) => setContractAddress(e.target.value)}
                    placeholder={`${placeholderText} or select from your tokens above`}
                    className="w-full bg-black/50 border-[var(--color-primary)]/50 text-white placeholder:text-[var(--color-primary)]/50"
                  />
                </div>
                <Button
                  onClick={validateContract}
                  disabled={isValidating}
                  className="w-full sm:w-auto bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/80 text-black font-semibold"
                >
                  {isValidating ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Scanning...
                    </>
                  ) : (
                    "Scan Contract"
                  )}
                </Button>
              </div>
              <div className="text-center text-sm text-[var(--color-primary)]">
                {getNetworkFeeDisplay()}
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Rest of your content */}
        {tokenInfo.name && (
          <div className="space-y-6">
            {/* Token Information */}
            <Card className="bg-black/30 backdrop-blur-sm border-[var(--color-header-accent)]">
              <CardHeader>
                <CardTitle className="text-xl">Token Information</CardTitle>
                <CardDescription className="text-gray-400">
                  Basic token details and contract information
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Tag className="h-4 w-4 text-[var(--color-primary)]" />
                        <Label>Token Name</Label>
                      </div>
                      <div className="flex items-center p-2 bg-black/50 border border-gray-700 rounded-md text-white overflow-hidden">
                        <div className="truncate text-sm w-full">
                          {tokenInfo.name || "Not Found"}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Coins className="h-4 w-4 text-[var(--color-primary)]" />
                        <Label>Token Symbol</Label>
                      </div>
                      <div className="flex items-center p-2 bg-black/50 border border-gray-700 rounded-md text-white overflow-hidden">
                        <div className="truncate text-sm w-full">
                          {tokenInfo.symbol || "Not Found"}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <BarChart className="h-4 w-4 text-[var(--color-primary)]" />
                        <Label>Total Supply</Label>
                      </div>
                      <div className="flex items-center p-2 bg-black/50 border border-gray-700 rounded-md text-white font-mono overflow-hidden">
                        <div className="truncate text-sm w-full">
                          {tokenInfo.totalSupply ? formatTokenAmount(tokenInfo.totalSupply, tokenInfo.decimals) : "Not Found"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trading Information */}
            <Card className="bg-black/30 backdrop-blur-sm border-[var(--color-header-accent)]">
              <CardHeader>
                <CardTitle className="text-xl">Trading Information</CardTitle>
                <CardDescription className="text-gray-400">
                  DEX and trading status details
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-4">
                  {/* Trading Status */}
                  <div className="space-y-2">
                    <div className="flex flex-col gap-2">
                      {/* Permanent Trading Status */}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Permanent Trading:</span>
                        <span className="flex items-center gap-2">
                          <span className={featureStatus.permanentTradingEnabled || (featureStatus.tradingEnabled && (featureStatus.tradingEnabledLocked || masterLocked || isOwnershipRenounced)) ? "text-[var(--color-primary)]" : "text-[var(--color-secondary)]"}>
                            {featureStatus.permanentTradingEnabled || (featureStatus.tradingEnabled && (featureStatus.tradingEnabledLocked || masterLocked || isOwnershipRenounced)) ? "Enabled" : "Disabled"}
                          </span>
                          {(featureStatus.permanentTradingEnabled || (featureStatus.tradingEnabled && (featureStatus.tradingEnabledLocked || masterLocked || isOwnershipRenounced))) && (
                            <span className="text-gray-500">
                              {featureStatus.permanentTradingEnabled ? "(Due to Contract Lock)" : 
                               featureStatus.tradingEnabledLocked ? "(Due to Trading Status Lock)" :
                               masterLocked ? "(Due to Contract Lock)" : 
                               isOwnershipRenounced ? "(Due to Renounced Ownership)" : ""}
                            </span>
                          )}
                        </span>
                      </div>

                      {/* Trading Status with Lock Status */}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Trading Status:</span>
                        <div className="flex items-center gap-2">
                          <span className={featureStatus.tradingEnabled ? "text-[var(--color-primary)]" : "text-[var(--color-secondary)]"}>
                            {featureStatus.tradingEnabled ? "Enabled" : "Disabled"}
                          </span>
                          {featureStatus.tradingEnabled && (
                            <span className="text-gray-500">
                              ({featureStatus.tradingEnabledLocked ? "Locked" : "Unlocked"})
                            </span>
                          )}
                        </div>
                      </div>

                      {/* DEX Pair */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <ArrowLeftRight className="h-4 w-4 text-[var(--color-header-accent)]" />
                          <Label>
                            {currentNetwork?.dexName || 'DEX'} Pair
                          </Label>
                        </div>
                        <div className="flex items-center p-2 bg-black/50 border border-gray-700 rounded-md text-white overflow-hidden">
                          <div className="truncate text-sm w-full">
                            {featureStatus.pancakePair || "Not Found"}
                          </div>
                        </div>
                      </div>

                      {/* DEX Router */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Network className="h-4 w-4 text-[var(--color-header-accent)]" />
                          <Label>
                            {currentNetwork?.dexName || 'DEX'} Router
                          </Label>
                        </div>
                        <div className="flex items-center p-2 bg-black/50 border border-gray-700 rounded-md text-white overflow-hidden">
                          <div className="truncate text-sm w-full">
                            {featureStatus.pancakeRouter || "Not Found"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tax Features Section */}
            {Object.keys(featureStatus).length > 0 && (
              <Card className="bg-black/30 backdrop-blur-sm border-[var(--color-primary)]">
                <CardHeader>
                  <CardTitle className="text-xl">Tax Features</CardTitle>
                  <CardDescription className="text-gray-400">
                    Contract tax settings and rates
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {['buyTax', 'sellTax'].map((featureId) => {
                    const status = featureStatus[featureId];
                    const feature = FEATURE_DEFINITIONS.find(f => f.id === featureId);
                    return (
                      <div key={featureId} className="p-4 bg-black/20 rounded-lg space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {feature?.icon}
                            <div>
                              <div className="font-medium">{feature?.name}</div>
                              <div className="text-sm text-gray-400">{feature?.description}</div>
                              <div className="text-sm text-gray-400">
                                Current Value: {status?.exists ? formatValue(status.value, featureId) : 'Not Set'}
                              </div>
                              <div className="text-sm text-gray-400">
                                Status: {status?.isLocked ? (
                                  <span className="text-[var(--color-primary)]">Locked</span>
                                ) : (
                                  <span className={`text-[var(--color-secondary)] relative ${
                                    masterLocked ? 'after:content-[""] after:absolute after:left-[-10%] after:right-[-10%] after:top-1/2 after:border-t-2 after:border-[var(--color-primary)]' : ''
                                  }`}>Unlocked</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Distribution Features Section */}
            {Object.keys(featureStatus).length > 0 && (
              <Card className="bg-black/30 backdrop-blur-sm border-[var(--color-header-accent)]">
                <CardHeader>
                  <CardTitle className="text-xl">Distribution Features</CardTitle>
                  <CardDescription className="text-gray-400">
                    Token distribution settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {['reflectionDistribution', 'autoLiquidityDistribution', 'burnDistribution', 'marketingDistribution', 'developmentDistribution'].map((featureId) => {
                    const status = featureStatus[featureId];
                    const feature = FEATURE_DEFINITIONS.find(f => f.id === featureId);
                    return (
                      <div key={featureId} className="p-4 bg-black/20 rounded-lg space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {feature?.icon}
                            <div>
                              <div className="font-medium">{feature?.name}</div>
                              <div className="text-sm text-gray-400">{feature?.description}</div>
                              <div className="text-sm text-gray-400">
                                Current Value: {status?.exists ? formatValue(status.value, featureId) : 'Not Set'}
                              </div>
                              <div className="text-sm text-gray-400">
                                Status: {status?.isLocked ? (
                                  <span className="text-[var(--color-primary)]">Locked</span>
                                ) : (
                                  <span className={`text-[var(--color-secondary)] relative ${
                                    masterLocked ? 'after:content-[""] after:absolute after:left-[-10%] after:right-[-10%] after:top-1/2 after:border-t-2 after:border-[var(--color-primary)]' : ''
                                  }`}>Unlocked</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Wallet Information Section */}
            {contract && (
              <Card className="bg-black/30 backdrop-blur-sm border-[var(--color-header-accent)]">
                <CardHeader>
                  <CardTitle className="text-xl">Wallet Information</CardTitle>
                  <CardDescription className="text-gray-400">
                    Marketing and Development wallet addresses and lock status
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(walletInfo).map(([type, info]) => (
                    <div key={type} className="p-4 bg-black/20 rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Wallet className="w-5 h-5 text-[var(--color-header-accent)]" />
                          <div>
                            <div className="font-medium capitalize">{type} Wallet</div>
                            <div className="text-xs text-[var(--color-header-accent)] mt-1 break-all">
                              {info.address || 'Not Set'}
                            </div>
                            <div className="text-sm text-gray-400 mt-1">
                              Status: {info.isLocked ? (
                                <span className="text-[var(--color-primary)]">Locked</span>
                              ) : (
                                <span className={`text-[var(--color-secondary)] relative ${
                                  masterLocked ? 'after:content-[""] after:absolute after:left-[-10%] after:right-[-10%] after:top-1/2 after:border-t-2 after:border-[var(--color-primary)]' : ''
                                }`}>Unlocked</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-xl">
                          {info.isLocked ? (
                            <Lock className="w-5 h-5 text-[var(--color-primary)]" />
                          ) : (
                            <Unlock className="w-5 h-5 text-[var(--color-secondary)]" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Limits Section */}
            {Object.keys(featureStatus).length > 0 && (
              <Card className="bg-black/30 backdrop-blur-sm border-[var(--color-primary)]">
                <CardHeader>
                  <CardTitle>Limits and Security</CardTitle>
                  <CardDescription>Contract limits and security features</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-4">
                    {FEATURE_DEFINITIONS.filter(feature => 
                      ['maxWallet', 'maxBuy', 'maxSell', 'tradingDelay'].includes(feature.id)
                    ).map(feature => {
                      const status = featureStatus[feature.id];
                      if (!status?.exists) return null;
                      
                      return (
                        <div key={feature.id} className="flex items-center justify-between p-4 bg-black/50 rounded-lg border border-gray-700">
                          <div className="flex items-center gap-3">
                            {feature.icon}
                            <div>
                              <h4 className="font-medium">{feature.name}</h4>
                              <p className="text-sm text-gray-400">{feature.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{formatValue(status.value, feature.id)}</span>
                            {status.isLocked && (
                              <Lock className="w-4 h-4 text-[var(--color-primary)]" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* HoneyPot Detector */}
            <HoneyPotDetector />
          </div>
        )}
        
        {/* Status Cards */}
        {tokenInfo.name && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <MasterLockStatus contract={contract} />
            <OwnershipStatus />
          </div>
        )}
        
        {/* Download Results Card */}
        {tokenInfo.name && (
          <Card className="mt-8 bg-black/30 backdrop-blur-sm border-[var(--color-primary)]/50 hover:border-[var(--color-primary)]/70 transition-all duration-200">
            <CardContent className="pt-6">
              <Button
                onClick={downloadScanResults}
                className="w-full bg-black/30 hover:bg-[var(--color-primary)]/10 text-white border border-[var(--color-primary)]/50 backdrop-blur-sm transition-all duration-200 hover:border-[var(--color-primary)]"
                size="lg"
              >
                <Download className="w-5 h-5 mr-2 text-[var(--color-primary)]" />
                Download Matrix Scan Results
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* FAQ Section */}
        <section className="py-12">
          <div className="container mx-auto">
            <div className="max-w-[900px] mx-auto"> {/* Match UI width */}
              <h2 className="text-3xl font-bold mb-8 tracking-tight text-center" style={{ color: 'var(--color-primary)' }}>
                Frequently Asked Questions
              </h2>
              <div className="rounded-lg overflow-hidden border border-[var(--color-primary)] bg-black/30 backdrop-blur-sm">
                {faqData.map((faq, index) => (
                  <FAQItem key={index} question={faq.question} answer={faq.answer} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Guides & Blog Section */}
        <section className="py-12">
          <div className="container mx-auto">
            <div className="max-w-[900px] mx-auto">
              <h2 className="text-3xl font-bold mb-8 tracking-tight text-center" style={{ color: 'var(--color-primary)' }}>
                Guides & Blog
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Create Token Guide */}
                <Link to="/blog/create-token-guide" className="group block">
                  <div className="p-4 rounded-lg border border-[var(--color-secondary)] hover:bg-[var(--color-secondary)]/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <BookOpen className="w-5 h-5 text-[var(--color-secondary)]" />
                      <span>Create Token Guide</span>
                    </div>
                  </div>
                </Link>

                {/* Create Meme Coin Guide */}
                <Link to="/blog/create-meme-coin" className="group block">
                  <div className="p-4 rounded-lg border border-[var(--color-secondary)] hover:bg-[var(--color-secondary)]/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <Rocket className="w-5 h-5 text-[var(--color-secondary)]" />
                      <span>Create Meme Coin Guide</span>
                    </div>
                  </div>
                </Link>

                {/* Create Reflection Token */}
                <Link to="/blog/create-reflection-token" className="group block">
                  <div className="p-4 rounded-lg border border-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <RefreshCw className="w-5 h-5 text-[var(--color-primary)]" />
                      <span>Create Reflection Token</span>
                    </div>
                  </div>
                </Link>

                {/* Create Auto-Liquidity Token */}
                <Link to="/blog/create-auto-liquidity-token" className="group block">
                  <div className="p-4 rounded-lg border border-[var(--color-header-accent)] hover:bg-[var(--color-header-accent)]/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <Droplet className="w-5 h-5 text-[var(--color-header-accent)]" />
                      <span>Create Auto-Liquidity Token</span>
                    </div>
                  </div>
                </Link>

                {/* Create Auto-Burn Token */}
                <Link to="/blog/create-auto-burn-token" className="group block">
                  <div className="p-4 rounded-lg border border-[var(--color-secondary)] hover:bg-[var(--color-secondary)]/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <Flame className="w-5 h-5 text-[var(--color-secondary)]" />
                      <span>Create Auto-Burn Token</span>
                    </div>
                  </div>
                </Link>

                {/* Matrix Contract Scanner */}
                <Link to="/blog/matrix-contract-scanner" className="group block">
                  <div className="p-4 rounded-lg border border-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <Search className="w-5 h-5 text-[var(--color-primary)]" />
                      <span>Matrix Contract Scanner</span>
                    </div>
                  </div>
                </Link>

                {/* Token Tools Guide */}
                <Link to="/blog/token-tools" className="group block">
                  <div className="p-4 rounded-lg border border-[var(--color-header-accent)] hover:bg-[var(--color-header-accent)]/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <Wrench className="w-5 h-5 text-[var(--color-header-accent)]" />
                      <span>Token Tools Guide</span>
                    </div>
                  </div>
                </Link>

                {/* Contract Locker Guide */}
                <Link to="/blog/contract-locker" className="group block">
                  <div className="p-4 rounded-lg border border-[var(--color-secondary)] hover:bg-[var(--color-secondary)]/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <Lock className="w-5 h-5 text-[var(--color-secondary)]" />
                      <span>Contract Locker Guide</span>
                    </div>
                  </div>
                </Link>

                {/* Contract Verification */}
                <Link to="/blog/automatic-contract-verification" className="group block">
                  <div className="p-4 rounded-lg border border-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-[var(--color-primary)]" />
                      <span>Contract Verification</span>
                    </div>
                  </div>
                </Link>

                {/* Smart Contract Locking Guide */}
                <Link to="/blog/smart-contract-locking" className="group block">
                  <div className="p-4 rounded-lg border border-[var(--color-header-accent)] hover:bg-[var(--color-header-accent)]/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <LockIcon className="w-5 h-5 text-[var(--color-header-accent)]" />
                      <span>Smart Contract Locking</span>
                    </div>
                  </div>
                </Link>

                {/* Token Economics Guide */}
                <Link to="/blog/token-economics" className="group block">
                  <div className="p-4 rounded-lg border border-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <BarChart3Icon className="w-5 h-5 text-[var(--color-primary)]" />
                      <span>Token Economics Guide</span>
                    </div>
                  </div>
                </Link>

                {/* Token Security Guide */}
                <Link to="/blog/token-security" className="group block">
                  <div className="p-4 rounded-lg border border-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-[var(--color-primary)]" />
                      <span>Token Security Guide</span>
                    </div>
                  </div>
                </Link>

                {/* Add Token Logo Guide */}
                <Link to="/blog/add-token-logo" className="group block">
                  <div className="p-4 rounded-lg border border-[var(--color-secondary)] hover:bg-[var(--color-secondary)]/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <FileCode2 className="w-5 h-5 text-[var(--color-secondary)]" />
                      <span>Add Token Logo Guide</span>
                    </div>
                  </div>
                </Link>

                {/* Best Cold Storage Wallets Guide */}
                <Link to="/blog/best-cold-storage-wallets" className="group block">
                  <div className="p-4 rounded-lg border border-[var(--color-header-accent)] hover:bg-[var(--color-header-accent)]/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <Wallet className="w-5 h-5 text-[var(--color-header-accent)]" />
                      <span>Best Cold Storage Wallets</span>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default MdbMatrixContractScan;
